import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FaceGuide } from "@/components/FaceGuide";
import { ScanFace, QrCode, Copy, RefreshCw, Camera, UserX, LogOut, Loader2, CheckCircle2, ClipboardList, SwitchCamera } from "lucide-react";
import { toast } from "sonner";
import { BulkFaceEnroll } from "@/components/BulkFaceEnroll";
import { FaceppBlockBanner, useFaceppStorage } from "@/components/FaceppBlockBanner";

const TYPE_LABEL = { CHEGADA: "Chegada", SAIDA_ANTECIPADA: "Saída antecipada", FALTA: "Falta" };
const TYPE_CLS = { CHEGADA: "bg-emerald-100 text-emerald-700", SAIDA_ANTECIPADA: "bg-amber-100 text-amber-700", FALTA: "bg-red-100 text-red-700" };
const today = () => new Date().toISOString().slice(0, 10);

export default function Presenca() {
  const { user, scopeParams, selectedSchoolId } = useAuth();
  const isGestao = ["SUPERADMIN", "DIRECAO", "COORDENACAO"].includes(user?.role);
  return (
    <div>
      <PageHeader title="Presença" subtitle="Quiosque de reconhecimento facial, registros e lançamentos manuais" />
      <Tabs defaultValue="registros">
        <div className="mb-5 w-full overflow-x-auto">
          <TabsList className="w-max" data-testid="presenca-tabs">
            <TabsTrigger value="registros" data-testid="tab-registros"><ClipboardList className="mr-1.5 h-4 w-4" /> Registros</TabsTrigger>
            <TabsTrigger value="manual" data-testid="tab-manual"><LogOut className="mr-1.5 h-4 w-4" /> Lançar manual</TabsTrigger>
            {isGestao && <TabsTrigger value="biometria" data-testid="tab-biometria"><Camera className="mr-1.5 h-4 w-4" /> Biometria</TabsTrigger>}
            {isGestao && <TabsTrigger value="quiosque" data-testid="tab-quiosque"><QrCode className="mr-1.5 h-4 w-4" /> Quiosque</TabsTrigger>}
          </TabsList>
        </div>
        <TabsContent value="registros"><RegistrosTab scopeParams={scopeParams} selectedSchoolId={selectedSchoolId} /></TabsContent>
        <TabsContent value="manual"><ManualTab scopeParams={scopeParams} selectedSchoolId={selectedSchoolId} isGestao={isGestao} /></TabsContent>
        {isGestao && <TabsContent value="biometria"><BiometriaPanel user={user} scopeParams={scopeParams} selectedSchoolId={selectedSchoolId} /></TabsContent>}
        {isGestao && <TabsContent value="quiosque"><QuiosqueTab user={user} selectedSchoolId={selectedSchoolId} /></TabsContent>}
      </Tabs>
    </div>
  );
}

function useStudents(scopeParams, selectedSchoolId) {
  const [students, setStudents] = useState([]);
  useEffect(() => {
    api.get("/students", { params: { ...scopeParams, status: "MATRICULADO" } }).then((r) => setStudents(r.data)).catch(() => setStudents([]));
  }, [scopeParams, selectedSchoolId]);
  return students;
}

function RegistrosTab({ scopeParams, selectedSchoolId }) {
  const [date, setDate] = useState(today());
  const [type, setType] = useState("all");
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    const p = { ...scopeParams, date };
    if (type !== "all") p.type = type;
    api.get("/attendance", { params: p }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [date, type, scopeParams]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="presenca-date" />
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-48" data-testid="presenca-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            <SelectItem value="CHEGADA">Chegada</SelectItem>
            <SelectItem value="SAIDA_ANTECIPADA">Saída antecipada</SelectItem>
            <SelectItem value="FALTA">Falta</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-400">{rows.length} registro(s)</span>
      </div>
      {rows.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Sem registros" description="Nenhum registro de presença para esta data." />
      ) : (
        <Card className="overflow-x-auto border-slate-200" data-testid="presenca-table">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Hora</TableHead><TableHead>Estudante</TableHead>
              <TableHead>Tipo</TableHead><TableHead>Origem</TableHead><TableHead>Obs.</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`presenca-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-sm font-medium text-slate-700">{r.time}</TableCell>
                  <TableCell className="text-sm text-slate-800">{r.student_name}</TableCell>
                  <TableCell><Badge className={TYPE_CLS[r.type]}>{TYPE_LABEL[r.type] || r.type}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-400">{r.method === "FACIAL" ? "Facial" : "Manual"}{r.recorded_by ? ` · ${r.recorded_by}` : ""}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.note || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function ManualTab({ scopeParams, selectedSchoolId, isGestao }) {
  const students = useStudents(scopeParams, selectedSchoolId);
  const [studentId, setStudentId] = useState("");
  const [type, setType] = useState("SAIDA_ANTECIPADA");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/attendance/manual", { student_id: studentId, type, note: note || undefined });
      toast.success("Registro lançado");
      setNote("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-xl space-y-5">
    <Card className="border-slate-200 p-5">
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600">Estudante</label>
          <Select value={studentId} onValueChange={setStudentId}>
            <SelectTrigger data-testid="manual-student-select"><SelectValue placeholder="Selecione o estudante" /></SelectTrigger>
            <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}{s.class_name ? ` · ${s.class_name}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600">Tipo</label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="manual-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="SAIDA_ANTECIPADA">Saída antecipada</SelectItem>
              <SelectItem value="FALTA">Falta</SelectItem>
              <SelectItem value="CHEGADA">Chegada</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-600">Observação (opcional)</label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex.: consulta médica" data-testid="manual-note" />
        </div>
        <Button onClick={submit} disabled={!studentId || busy} className="bg-blue-700 hover:bg-blue-800" data-testid="manual-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Lançar registro"}
        </Button>
      </div>
    </Card>
    {isGestao && <AutoFaltaCard scopeParams={scopeParams} selectedSchoolId={selectedSchoolId} />}
    </div>
  );
}

function AutoFaltaCard({ scopeParams, selectedSchoolId }) {
  const [date, setDate] = useState(today());
  const [classId, setClassId] = useState("all");
  const [classes, setClasses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/classes", { params: scopeParams }).then((r) => setClasses(r.data)).catch(() => setClasses([])); }, [scopeParams, selectedSchoolId]);
  const fmt = (d) => d.split("-").reverse().join("/");
  const run = async () => {
    setBusy(true);
    try {
      const params = { date };
      if (scopeParams.school_id) params.school_id = scopeParams.school_id;
      if (classId !== "all") params.class_id = classId;
      const { data } = await api.post("/attendance/generate-absences", null, { params });
      toast.success(`${data.created} falta(s) gerada(s) para ${fmt(data.date)}`);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card className="border-slate-200 p-5" data-testid="auto-falta-card">
      <div className="mb-1 flex items-center gap-2"><UserX className="h-5 w-5 text-rose-600" /><h3 className="font-display font-semibold text-slate-800">Falta automática do dia</h3></div>
      <p className="mb-4 text-sm text-slate-500">Marca FALTA para todos os alunos matriculados sem CHEGADA registrada na data. Não afeta quem já tem chegada ou falta lançada. Professores/gestão podem corrigir manualmente depois.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="mb-1 block text-xs font-medium text-slate-600">Data</label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="auto-falta-date" /></div>
        <div><label className="mb-1 block text-xs font-medium text-slate-600">Turma (opcional)</label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="w-56" data-testid="auto-falta-class"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas as turmas</SelectItem>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-rose-600 hover:bg-rose-700" data-testid="auto-falta-button"><UserX className="mr-1.5 h-4 w-4" /> Gerar faltas do dia</Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="auto-falta-dialog">
          <DialogHeader><DialogTitle>Gerar faltas automáticas</DialogTitle>
            <DialogDescription>Confirmar lançamento de FALTA para os alunos sem chegada em {fmt(date)}{classId !== "all" ? " (turma selecionada)" : ""}?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={run} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="auto-falta-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gerar faltas"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function BiometriaPanel({ user, scopeParams, selectedSchoolId }) {
  const fp = useFaceppStorage(scopeParams.school_id);
  return (
    <>
      <FaceppBlockBanner st={fp.st} blocked={fp.blocked} checking={fp.checking} onRecheck={fp.recheck} />
      <BiometriaTab scopeParams={scopeParams} selectedSchoolId={selectedSchoolId} blocked={fp.blocked} />
      {user?.role === "SUPERADMIN" && <BulkFaceEnroll scopeParams={scopeParams} blocked={fp.blocked} />}
    </>
  );
}

function BiometriaTab({ scopeParams, selectedSchoolId, blocked }) {
  const students = useStudents(scopeParams, selectedSchoolId);
  const [studentId, setStudentId] = useState("");
  const [open, setOpen] = useState(false);
  const selected = students.find((s) => s.id === studentId);
  return (
    <div className="max-w-xl">
      <Card className="border-slate-200 p-5">
        <p className="mb-4 text-sm text-slate-500">Cadastre a foto facial do estudante. A imagem é enviada ao CompreFace da escola e usada no quiosque de chegada.</p>
        <label className="mb-1 block text-sm font-medium text-slate-600">Estudante</label>
        <Select value={studentId} onValueChange={setStudentId}>
          <SelectTrigger data-testid="bio-student-select"><SelectValue placeholder="Selecione o estudante" /></SelectTrigger>
          <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}{s.class_name ? ` · ${s.class_name}` : ""}</SelectItem>)}</SelectContent>
        </Select>
        <Button className="mt-4 bg-blue-700 hover:bg-blue-800" disabled={!studentId || blocked} onClick={() => setOpen(true)} data-testid="bio-capture-open">
          <Camera className="mr-2 h-4 w-4" /> Capturar / enviar foto
        </Button>
      </Card>
      {selected && <EnrollDialog open={open} setOpen={setOpen} student={selected} />}
    </div>
  );
}

export function EnrollDialog({ open, setOpen, student, onDone }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [mode, setMode] = useState("camera");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [facing, setFacing] = useState("environment");

  const stop = useCallback(() => { if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; } }, []);
  useEffect(() => {
    if (!open) { stop(); setPreview(null); return; }
    if (mode === "camera") {
      navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: facing } } }).then((s) => {
        streamRef.current = s; if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play(); }
      }).catch(() => toast.error("Não foi possível acessar a câmera"));
    }
    return () => stop();
  }, [open, mode, facing, stop]);

  const snap = () => {
    const v = videoRef.current; if (!v?.videoWidth) return;
    const c = document.createElement("canvas"); c.width = 480; c.height = 480;
    const ctx = c.getContext("2d");
    const side = Math.min(v.videoWidth, v.videoHeight);
    ctx.drawImage(v, (v.videoWidth - side) / 2, (v.videoHeight - side) / 2, side, side, 0, 0, 480, 480);
    setPreview(c.toDataURL("image/jpeg", 0.85));
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader(); reader.onload = () => setPreview(reader.result); reader.readAsDataURL(f);
  };
  const enroll = async () => {
    setBusy(true);
    try {
      await api.post(`/students/${student.id}/face-enroll`, { image_base64: preview });
      toast.success(`Foto facial cadastrada para ${student.full_name}`);
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="enroll-dialog">
        <DialogHeader><DialogTitle>Cadastrar face — {student.full_name}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Captura de foto facial do estudante</DialogDescription>
        <div className="flex gap-2">
          <Button size="sm" variant={mode === "camera" ? "default" : "outline"} onClick={() => { setMode("camera"); setPreview(null); }} data-testid="enroll-mode-camera">Câmera</Button>
          <Button size="sm" variant={mode === "upload" ? "default" : "outline"} onClick={() => { stop(); setMode("upload"); setPreview(null); }} data-testid="enroll-mode-upload">Enviar arquivo</Button>
        </div>
        <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-black">
          {preview ? <img src={preview} alt="preview" className="aspect-square w-full object-cover" />
            : mode === "camera" ? <video ref={videoRef} autoPlay muted playsInline className="aspect-square w-full object-cover" />
            : <div className="grid aspect-square w-full place-items-center bg-slate-50"><input type="file" accept="image/*" onChange={onFile} data-testid="enroll-file-input" /></div>}
          {mode === "camera" && !preview && <FaceGuide />}
        </div>
        <div className="flex gap-2">
          {mode === "camera" && !preview && <Button variant="outline" onClick={snap} data-testid="enroll-snap"><Camera className="mr-2 h-4 w-4" /> Capturar</Button>}
          {mode === "camera" && !preview && (
            <Button variant="outline" onClick={() => { stop(); setFacing((f) => (f === "environment" ? "user" : "environment")); }} data-testid="enroll-switch-camera">
              <SwitchCamera className="mr-2 h-4 w-4" />{facing === "environment" ? "Usar frontal" : "Usar traseira"}
            </Button>
          )}
          {preview && <Button variant="outline" onClick={() => setPreview(null)} data-testid="enroll-retake">Refazer</Button>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={enroll} disabled={!preview || busy} className="bg-blue-700 hover:bg-blue-800" data-testid="enroll-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-2 h-4 w-4" /> Cadastrar</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuiosqueTab({ user, selectedSchoolId }) {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const schoolId = user?.role === "SUPERADMIN" ? selectedSchoolId : user?.school_id;
  const load = useCallback(() => {
    if (!schoolId) { setLoading(false); return; }
    setLoading(true);
    api.get(`/schools/${schoolId}/kiosk-token`).then((r) => setToken(r.data.kiosk_token)).catch(() => setToken(null)).finally(() => setLoading(false));
  }, [schoolId]);
  useEffect(() => { load(); }, [load]);
  const generate = async () => {
    try { const { data } = await api.post(`/schools/${schoolId}/kiosk-token`); setToken(data.kiosk_token); toast.success("Token do quiosque gerado"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const url = token ? `${window.location.origin}/quiosque?token=${token}` : "";
  const copy = () => { navigator.clipboard.writeText(url); toast.success("Link copiado"); };

  if (!schoolId) return <EmptyState icon={QrCode} title="Selecione uma escola" description="Superadmin: selecione uma escola no topo para gerenciar o quiosque." />;
  return (
    <Card className="max-w-2xl border-slate-200 p-5">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-blue-50 text-blue-700"><ScanFace className="h-6 w-6" /></div>
        <div>
          <div className="font-display text-lg font-bold text-slate-900">Link do Quiosque de Presença</div>
          <div className="text-xs text-slate-400">Abra este link no tablet/computador da entrada da escola (não exige login).</div>
        </div>
      </div>
      {loading ? <Loader2 className="mt-5 h-5 w-5 animate-spin text-blue-700" /> : token ? (
        <div className="mt-5 space-y-3">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <input readOnly value={url} className="flex-1 bg-transparent text-sm text-slate-700 outline-none" data-testid="kiosk-url" />
            <Button size="sm" variant="outline" onClick={copy} data-testid="kiosk-copy"><Copy className="h-4 w-4" /></Button>
          </div>
          <div className="flex gap-2">
            <a href={url} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="kiosk-open"><ScanFace className="mr-2 h-4 w-4" /> Abrir quiosque</Button></a>
            <Button variant="ghost" onClick={generate} data-testid="kiosk-regenerate"><RefreshCw className="mr-2 h-4 w-4" /> Gerar novo (invalida o atual)</Button>
          </div>
        </div>
      ) : (
        <Button className="mt-5 bg-blue-700 hover:bg-blue-800" onClick={generate} data-testid="kiosk-generate"><QrCode className="mr-2 h-4 w-4" /> Gerar link do quiosque</Button>
      )}
    </Card>
  );
}
